import React, { useEffect, useState } from "react";
import useTranslate from "../../hooks/useTranslate";

// Report flow (QAL-T6). Desktop: centered 440px dialog (frame 1h·1).
// Mobile: bottom sheet (frame 1k·2). Exactly 3 fixed reasons — codes from
// the T1 Report model, labels verbatim from frame 1h. The first reason
// starts selected (both frames draw it selected; one reason, one click).
//
// The common/BottomSheet component draws an X-close header and different
// grabber tokens than the 1k sheet, so — like the T3 channel sheet — the
// drawn sheet stays an inline variant here.
//
// Comment variant title "Report this comment" is the workorder-sanctioned
// copy fill (the frames draw only the post title).

export const REPORT_REASONS = [
  { code: "named_entity", label: "Club, player or active deal named" },
  { code: "not_craft", label: "Not agent craft, spam or self-promo" },
  { code: "abusive", label: "Abusive or unprofessional" },
];

const Radio = ({ selected }) =>
  selected ? (
    <span className="w-4 h-4 rounded-full border-[5px] border-[#2283FF] bg-white shrink-0 box-border" />
  ) : (
    <span className="w-4 h-4 rounded-full border-[1.5px] border-[#C6CFDA] shrink-0 box-border" />
  );

const ReasonRows = ({ reason, setReason, t, mobile }) => (
  <div
    role="radiogroup"
    aria-label={t("Reason")}
    className={`flex flex-col gap-1 ${mobile ? "mt-3" : "mt-3.5"}`}
  >
    {REPORT_REASONS.map((r) => {
      const selected = reason === r.code;
      return (
        <button
          key={r.code}
          type="button"
          role="radio"
          aria-checked={selected}
          onClick={() => setReason(r.code)}
          data-testid={`report-reason-${r.code}`}
          className={`flex items-center gap-2.5 px-3 rounded-[10px] text-left cursor-pointer ${
            mobile ? "py-[11px]" : "py-[9px]"
          } ${
            selected
              ? "border border-[#2283FF] bg-[#F4F8FF]"
              : "border border-transparent bg-transparent hover:bg-[#F8FAFC]"
          }`}
        >
          <Radio selected={selected} />
          <span
            className={`text-[13.5px] text-[#111827] ${selected ? "font-medium" : ""}`}
          >
            {t(r.label)}
          </span>
        </button>
      );
    })}
  </div>
);

const LoungeReportDialog = ({ open, type = "post", submitting = false, onCancel, onSubmit }) => {
  const { t } = useTranslate();
  const [reason, setReason] = useState(REPORT_REASONS[0].code);

  // A fresh dialog starts on the first reason again (1h·1).
  useEffect(() => {
    if (open) setReason(REPORT_REASONS[0].code);
  }, [open]);

  if (!open) return null;

  const title = type === "post" ? t("Report this post") : t("Report this comment");
  const sub = t("Reports go to the TransferPitch team. The author is not notified.");
  const submit = () => {
    if (!submitting) onSubmit(reason);
  };

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" data-testid="report-dialog">
      <div
        className="absolute inset-0 bg-[rgba(17,24,39,0.40)]"
        onClick={onCancel}
        aria-hidden="true"
      />

      {/* Desktop — centered 440px dialog (1h·1). */}
      <div className="hidden sm:flex absolute inset-0 items-center justify-center p-6 pointer-events-none">
        <div className="pointer-events-auto bg-white rounded-[14px] shadow-[0_24px_64px_rgba(17,24,39,0.25)] py-[22px] px-6 w-[440px] max-w-full">
          <p className="m-0 text-[15px] font-bold text-[#111827]">{title}</p>
          <p className="m-0 mt-[5px] text-[13px] leading-[1.5] text-[#6B7280]">{sub}</p>
          <ReasonRows reason={reason} setReason={setReason} t={t} mobile={false} />
          <div className="flex justify-end gap-2.5 mt-[18px]">
            <button
              type="button"
              onClick={onCancel}
              className="bg-white border border-[#DDE3EB] rounded-[10px] py-[9px] px-[18px] text-[13.5px] font-semibold text-[#111827] cursor-pointer hover:border-[#C6CFDA]"
            >
              {t("Cancel")}
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={submitting}
              data-testid="report-submit"
              className={`bg-[#2283FF] text-white border-none rounded-[10px] py-[9px] px-5 text-[13.5px] font-semibold ${
                submitting ? "opacity-50 cursor-default" : "cursor-pointer hover:bg-[#1B6FE0]"
              }`}
            >
              {t("Report")}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile — bottom sheet (1k·2): grabber, stacked full-width
          Report + Cancel, 44px targets. */}
      <div className="sm:hidden absolute left-0 right-0 bottom-0 bg-white rounded-t-2xl shadow-[0_-12px_40px_rgba(17,24,39,0.18)] pt-2.5 px-4 pb-6 max-h-[85vh] overflow-y-auto">
        <div className="w-10 h-1 rounded-full bg-[#DDE3EB] mx-auto" />
        <p className="m-0 mt-3.5 text-[15px] font-bold text-[#111827]">{title}</p>
        <p className="m-0 mt-1 text-[13px] leading-[1.5] text-[#6B7280]">{sub}</p>
        <ReasonRows reason={reason} setReason={setReason} t={t} mobile />
        <button
          type="button"
          onClick={submit}
          disabled={submitting}
          data-testid="report-submit-mobile"
          className={`w-full mt-3.5 bg-[#2283FF] text-white border-none rounded-[10px] h-11 text-[13.5px] font-semibold ${
            submitting ? "opacity-50" : ""
          }`}
        >
          {t("Report")}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="w-full mt-2 bg-white border border-[#DDE3EB] rounded-[10px] h-11 text-[13.5px] font-semibold text-[#6B7280]"
        >
          {t("Cancel")}
        </button>
      </div>
    </div>
  );
};

export default LoungeReportDialog;
